import React, { useEffect, useMemo, useState } from 'react';

const roleKeys = ['teacher', 'coach', 'guardian', 'sponsor', 'prospect', 'athlete', 'student', 'admin'] as const;
type RoleKey = (typeof roleKeys)[number];

type RolePricing = {
  price_credits: number | null;
  price_cents: number | null;
} | null;

type ClaimCounts = {
  pending: number;
  fulfilled: number;
  cancelled: number;
  unknown: number;
};

type MarketItem = {
  id: string;
  sku: string;
  kind: string;
  title: string;
  description: string;
  preview_url?: string | null;
  meta?: Record<string, any>;
  active: boolean;
  rolePricing: Record<RoleKey, RolePricing>;
  claim_counts?: ClaimCounts | null;
};

type PricingState = Record<RoleKey, { enabled: boolean; price_credits: number | '' }>;

type DraftItem = {
  id: string | null;
  sku: string;
  kind: string;
  title: string;
  description: string;
  preview_url?: string;
  meta: Record<string, any>;
  active: boolean;
  pricing: PricingState;
};

type MarketItemsPanelProps = {
  bearer?: Record<string, string> | null;
};

type OfferClaim = {
  id: string;
  offer_id: string;
  user_id: string;
  sponsor_id: string | null;
  status: string;
  fulfillment_meta?: Record<string, any>;
  created_at?: string;
};

const defaultPricing = (): PricingState => {
  return roleKeys.reduce((acc, role) => {
    acc[role] = { enabled: false, price_credits: 0 };
    return acc;
  }, {} as PricingState);
};

const emptyForm: DraftItem = {
  id: null,
  sku: '',
  kind: 'badge',
  title: '',
  description: '',
  preview_url: '',
  meta: {},
  active: true,
  pricing: defaultPricing(),
};

function buildDraftFromItem(item?: MarketItem | null): DraftItem {
  if (!item) return { ...emptyForm, pricing: defaultPricing() };
  const pricing = defaultPricing();
  for (const role of roleKeys) {
    const rp = item.rolePricing[role];
    if (rp) {
      pricing[role] = { enabled: true, price_credits: rp.price_credits ?? 0 };
    }
  }
  return {
    id: item.id,
    sku: item.sku,
    kind: item.kind,
    title: item.title,
    description: item.description,
    preview_url: item.preview_url || '',
    meta: item.meta || {},
    active: item.active,
    pricing,
  };
}

function buildPayloadFromDraft(draft: DraftItem) {
  const pricingPayload: Record<RoleKey, { enabled: boolean; price_credits: number }> =
    {} as Record<RoleKey, { enabled: boolean; price_credits: number }>;

  for (const role of roleKeys) {
    const pr = draft.pricing[role];
    pricingPayload[role] = {
      enabled: !!pr.enabled,
      price_credits: pr.enabled ? Number(pr.price_credits || 0) : 0,
    };
  }

  return {
    id: draft.id || undefined,
    sku: draft.sku,
    kind: draft.kind,
    title: draft.title,
    description: draft.description,
    preview_url: draft.preview_url,
    meta: draft.meta || {},
    active: draft.active,
    pricing: pricingPayload,
  };
}

export function MarketItemsPanel({ bearer }: MarketItemsPanelProps) {
  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DraftItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState<string>('all');

  const [viewingOffer, setViewingOffer] = useState<MarketItem | null>(null);
  const [offerClaims, setOfferClaims] = useState<OfferClaim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsError, setClaimsError] = useState<string | null>(null);

  const canLoad = Boolean(bearer);

  const filteredItems = useMemo(() => {
    if (filterKind === 'all') return items;
    return items.filter((it) => it.kind === filterKind);
  }, [filterKind, items]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!canLoad) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/v1/admin/marketplace/items${filterKind && filterKind !== 'all' ? `?kind=${filterKind}` : ''}`,
          { headers: { ...bearer, 'Content-Type': 'application/json' } },
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Unable to load catalog');
        }
        if (!cancelled) {
          setItems(data.items || []);
        }
      } catch (err: any) {
        console.error('admin-market-items error', err);
        if (!cancelled) {
          setError(err?.message || 'Unable to load catalog');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [bearer, canLoad, filterKind]);

  async function refreshItems() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/admin/marketplace/items${filterKind && filterKind !== 'all' ? `?kind=${filterKind}` : ''}`,
        { headers: { ...bearer, 'Content-Type': 'application/json' } },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to load catalog');
      }
      setItems(data.items || []);
    } catch (err: any) {
      setError(err?.message || 'Unable to load catalog');
    } finally {
      setLoading(false);
    }
  }

  const openNew = () => setEditing({ ...emptyForm, pricing: defaultPricing() });

  const openEdit = (item: MarketItem) => {
    setEditing(buildDraftFromItem(item));
  };

  async function saveDraft(draft: DraftItem) {
    if (!bearer) return;
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayloadFromDraft(draft);
      const res = await fetch('/api/v1/admin/marketplace/items/upsert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...bearer },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || 'Save failed');
      }
      setEditing(null);
      await refreshItems();
    } catch (e: any) {
      console.error('save error', e);
      setError(e?.message || 'Save error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(item: MarketItem) {
    const draft = buildDraftFromItem(item);
    draft.active = !item.active;
    await saveDraft(draft);
  }

  async function loadClaims(offer: MarketItem) {
    if (!bearer) return;
    setViewingOffer(offer);
    setClaimsLoading(true);
    setClaimsError(null);
    setOfferClaims([]);
    try {
      const res = await fetch(`/api/v1/admin/special-offers/${offer.id}/claims`, {
        headers: { ...bearer, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || 'Unable to load claims');
      }
      setOfferClaims(json.claims || []);
    } catch (err: any) {
      console.error('claims load error', err);
      setClaimsError(err?.message || 'Unable to load claims');
    } finally {
      setClaimsLoading(false);
    }
  }

  async function updateClaimStatus(claimId: string, status: 'fulfilled' | 'pending' | 'cancelled') {
    if (!bearer) return;
    setClaimsError(null);
    try {
      const res = await fetch('/api/v1/admin/special-offers/claims/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...bearer },
        body: JSON.stringify({ claim_id: claimId, status }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || 'Failed to update claim');
      }
      if (viewingOffer) {
        await loadClaims(viewingOffer);
        await refreshItems();
      }
    } catch (err: any) {
      console.error('claim update error', err);
      setClaimsError(err?.message || 'Failed to update claim');
    }
  }

  if (loading) return <div>Loading catalog…</div>;
  if (error) return <div className="text-sm text-red-600">{error}</div>;

  const specialOffers = items.filter((it) => it.kind === 'special_offer');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Marketplace Items</h2>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Kind
          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="badge">Badge</option>
            <option value="avatar">Avatar</option>
            <option value="theme">Theme</option>
            <option value="border">Border</option>
            <option value="digital_good">Digital good</option>
            <option value="ai_tool">AI tool</option>
            <option value="special_offer">Special offer</option>
            <option value="credit_pack">Credit pack</option>
          </select>
        </label>
        <button onClick={openNew} className="px-3 py-1 rounded bg-blue-600 text-white text-sm ml-auto">
          New item
        </button>
      </div>

      <div className="border rounded-md divide-y">
        {filteredItems.map((it) => (
          <div key={it.id} className="flex flex-col gap-2 px-3 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{it.title}</span>
                <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{it.kind}</span>
                {!it.active && <span className="text-xs text-red-600">Inactive</span>}
              </div>
              <div className="text-xs text-slate-500">SKU: {it.sku}</div>
              <div className="flex flex-wrap gap-2 text-[11px] text-slate-600">
                {roleKeys.map((role) => {
                  const rolePrice = it.rolePricing[role];
                  if (!rolePrice) return null;
                  return (
                    <span key={role} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                      <span className="uppercase">{role}</span>
                      <span className="font-semibold">{rolePrice.price_credits ?? 0}</span>
                      <span>cr</span>
                    </span>
                  );
                })}
              </div>
              {it.kind === 'special_offer' && (
                <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                  <span>Remaining: {it.meta?.quantity_remaining ?? '—'}</span>
                  <span>Total: {it.meta?.quantity_total ?? '—'}</span>
                  {typeof it.meta?.cash_value_cents === 'number' && (
                    <span>
                      Cash value: ${(Number(it.meta.cash_value_cents || 0) / 100).toFixed(2)}
                    </span>
                  )}
                  {it.claim_counts && (
                    <span>
                      Claims — pending: {it.claim_counts.pending}, fulfilled: {it.claim_counts.fulfilled}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {it.kind === 'special_offer' && (
                <button
                  onClick={() => loadClaims(it)}
                  className="text-xs px-2 py-1 border rounded-md"
                  type="button"
                >
                  Claims
                </button>
              )}
              <button
                onClick={() => openEdit(it)}
                className="text-xs px-2 py-1 border rounded-md"
                type="button"
              >
                Edit
              </button>
              <button
                onClick={() => toggleActive(it)}
                className="text-xs px-2 py-1 rounded-md border"
                type="button"
              >
                {it.active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="px-3 py-4 text-sm text-slate-500">No marketplace items yet.</div>
        )}
      </div>

      {specialOffers.length > 0 && (
        <div className="space-y-2 rounded-md border p-3 bg-slate-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Special offers overview</h3>
          </div>
          <div className="grid gap-2 text-xs">
            {specialOffers.map((offer) => (
              <div key={offer.id} className="flex flex-wrap items-center justify-between rounded bg-white p-2 shadow-sm">
                <div className="space-y-1">
                  <div className="font-medium text-slate-800">{offer.title}</div>
                  <div className="text-slate-600 flex flex-wrap gap-3">
                    <span>Remaining: {offer.meta?.quantity_remaining ?? '—'}</span>
                    <span>Total: {offer.meta?.quantity_total ?? '—'}</span>
                    {offer.claim_counts && (
                      <span>
                        Claims — pending: {offer.claim_counts.pending}, fulfilled: {offer.claim_counts.fulfilled}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="text-xs px-2 py-1 rounded border"
                  type="button"
                  onClick={() => loadClaims(offer)}
                >
                  Manage claims
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-4 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-sm">{editing.id ? 'Edit item' : 'New item'}</h3>
              <button onClick={() => setEditing(null)} className="text-xs text-slate-500">
                Close
              </button>
            </div>

            <div className="space-y-2 text-sm">
              <label className="block">
                <span className="text-xs text-slate-600">SKU</span>
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={editing.sku}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Kind</span>
                <select
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                >
                  <option value="badge">Badge</option>
                  <option value="avatar">Avatar</option>
                  <option value="theme">Theme</option>
                  <option value="border">Border</option>
                  <option value="digital_good">Digital good</option>
                  <option value="ai_tool">AI tool</option>
                  <option value="special_offer">Special offer</option>
                  <option value="credit_pack">Credit pack</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Title</span>
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Description</span>
                <textarea
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  rows={3}
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Preview URL</span>
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={editing.preview_url || ''}
                  onChange={(e) => setEditing({ ...editing, preview_url: e.target.value })}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-xs mt-1">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                <span>Active</span>
              </label>
            </div>

            <div className="mt-3 border-t pt-3 text-sm">
              <div className="font-medium mb-2">Pricing per role (credits)</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {roleKeys.map((role) => (
                  <div key={role} className="flex flex-col gap-1 border rounded p-2">
                    <label className="inline-flex items-center gap-2 text-xs font-semibold">
                      <input
                        type="checkbox"
                        checked={editing.pricing[role].enabled}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            pricing: {
                              ...editing.pricing,
                              [role]: {
                                ...editing.pricing[role],
                                enabled: e.target.checked,
                              },
                            },
                          })
                        }
                      />
                      <span className="uppercase">{role}</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="w-full border rounded px-2 py-1 text-sm"
                      value={editing.pricing[role].price_credits}
                      disabled={!editing.pricing[role].enabled}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          pricing: {
                            ...editing.pricing,
                            [role]: {
                              ...editing.pricing[role],
                              price_credits: Number(e.target.value || 0),
                            },
                          },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {editing.kind === 'special_offer' && (
              <div className="mt-3 border-t pt-3 text-sm space-y-2">
                <div className="font-medium">Special offer settings</div>
                <label className="block">
                  <span className="text-xs text-slate-600">Quantity total</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border rounded px-2 py-1 text-sm"
                    value={editing.meta?.quantity_total ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        meta: { ...editing.meta, quantity_total: Number(e.target.value || 0) },
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Quantity remaining</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border rounded px-2 py-1 text-sm"
                    value={editing.meta?.quantity_remaining ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        meta: { ...editing.meta, quantity_remaining: Number(e.target.value || 0) },
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Cash value (cents)</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border rounded px-2 py-1 text-sm"
                    value={editing.meta?.cash_value_cents ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        meta: { ...editing.meta, cash_value_cents: Number(e.target.value || 0) },
                      })
                    }
                  />
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button className="text-xs px-3 py-1 rounded border" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button
                className="text-xs px-3 py-1 rounded bg-blue-600 text-white"
                onClick={() => editing && saveDraft(editing)}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingOffer && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="text-sm font-semibold">Claims for {viewingOffer.title}</div>
                <div className="text-xs text-slate-500">Review and fulfill claims</div>
              </div>
              <button onClick={() => setViewingOffer(null)} className="text-xs text-slate-500">
                Close
              </button>
            </div>

            {claimsLoading && <div className="text-sm text-slate-600">Loading claims…</div>}
            {claimsError && <div className="text-sm text-red-600">{claimsError}</div>}

            {!claimsLoading && !offerClaims.length && !claimsError && (
              <div className="text-sm text-slate-500">No claims yet.</div>
            )}

            {!claimsLoading && offerClaims.length > 0 && (
              <div className="divide-y rounded border">
                {offerClaims.map((claim) => (
                  <div key={claim.id} className="flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                      <div className="font-medium">{claim.status}</div>
                      <div className="text-xs text-slate-500">Claim ID: {claim.id}</div>
                      {claim.created_at && (
                        <div className="text-xs text-slate-500">{new Date(claim.created_at).toLocaleString()}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {claim.status !== 'fulfilled' && (
                        <button
                          className="text-xs px-2 py-1 rounded border"
                          onClick={() => updateClaimStatus(claim.id, 'fulfilled')}
                          type="button"
                        >
                          Mark fulfilled
                        </button>
                      )}
                      {claim.status !== 'pending' && (
                        <button
                          className="text-xs px-2 py-1 rounded border"
                          onClick={() => updateClaimStatus(claim.id, 'pending')}
                          type="button"
                        >
                          Mark pending
                        </button>
                      )}
                      {claim.status !== 'cancelled' && (
                        <button
                          className="text-xs px-2 py-1 rounded border"
                          onClick={() => updateClaimStatus(claim.id, 'cancelled')}
                          type="button"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketItemsPanel;
